import { createQueryKey } from '../query/createQueryKeyFactory';
import { QueryKey, QueryKeyParam } from '../query/types';
import { benefitKeys } from './campaigns/benefits/keys';
import { itemKeys } from './carts/items/keys';
import { categoryKeys } from './categories/keys';
import { consumerKeys } from './consumer/keys';
import { discountsKeys } from './discounts/keys';
import { eventsKeys } from './events/keys';
import { inventoryKeys } from './inventory/keys';
import { storeItemStudentFeesKeys } from './items/studentFees/keys';
import { membershipsKeys } from './memberships/keys';
import { additionalFieldPresetsKeys } from './options/additionalFields/keys';
import { productOptionPresetsKeys } from './options/productOptions/keys';
import { redeemableTicketsKeys } from './redeemableTickets/keys';

const terminalCartKeys = (scopeId: QueryKeyParam) => {
  const keys = ['store', scopeId, 'carts', 'terminal'] as const;

  return createQueryKey({
    all: [...keys] as const,
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: [...keys, itemId] as const,
        detail: [...keys, itemId, 'detail'] as const,
      }),
    items: itemKeys([...keys, scopeId] as const),
  });
};

/** Generic Cart Keys  */
const cartKeys = (scopeId: QueryKeyParam) => {
  const keys = ['store', scopeId, 'carts'] as const;
  return createQueryKey({
    all: ['store', scopeId, 'carts'] as const,
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'carts', itemId] as const,
        detail: ['store', scopeId, 'carts', itemId, 'detail'] as const,
      }),
    items: itemKeys([...keys, scopeId] as const),
    export: [...keys, 'carts', 'export'] as const,
  });
};

const fundRaiserKeys = (scopeId: QueryKeyParam) =>
  createQueryKey({
    all: ['store', scopeId, 'items', 'fundraisers'] as const,
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'fundraisers', itemId] as const,
        detail: ['store', scopeId, 'fundraisers', itemId, 'detail'] as const,
      }),
  });

const donationKeys = (scopeId: QueryKeyParam) =>
  createQueryKey({
    all: ['store', scopeId, 'items', 'donations'] as const,
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'donations', itemId] as const,
        detail: ['store', scopeId, 'donations', itemId, 'detail'] as const,
      }),
  });

const subscriptionKeys = (scopeId: QueryKeyParam) =>
  createQueryKey({
    all: ['store', scopeId, 'subscriptions'] as const,
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'subscriptions', itemId] as const,
        detail: ['store', scopeId, 'subscriptions', itemId, 'detail'] as const,
      }),
  });

const campaignCartKeys = (scopeId: QueryKeyParam, itemId: QueryKeyParam) =>
  createQueryKey({
    all: ['store', scopeId, 'items', 'campaigns', itemId, 'carts'] as const,
    id: (cartId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'items', 'campaigns', itemId, 'carts', cartId] as const,
        detail: [
          'store',
          scopeId,
          'items',
          'campaigns',
          itemId,
          'carts',
          cartId,
          'detail',
        ] as const,
      }),
    export: ['store', scopeId, 'items', 'campaigns', itemId, 'carts', 'export'] as const,
  });
/** Query Keys for ['store', scopedId, items,  campaigns, *] */
const campaignsKeys = (scopeId: QueryKeyParam) =>
  createQueryKey({
    all: ['store', scopeId, 'items', 'campaigns'] as const,
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'items', 'campaigns', itemId] as const,
        detail: ['store', scopeId, 'items', 'campaigns', itemId, 'detail'] as const,
        recentDonations: [
          'store',
          scopeId,
          'items',
          'campaigns',
          itemId,
          'recent_donations',
        ] as const,
        carts: campaignCartKeys(scopeId, itemId),
        participants: createQueryKey({
          export: [
            'store',
            scopeId,
            'items',
            'campaigns',
            itemId,
            'participants',
            'export',
          ] as const,
          all: ['store', scopeId, 'items', 'campaigns', itemId, 'participants'] as const,
          id(id) {
            return createQueryKey({
              all: ['store', scopeId, 'items', 'campaigns', itemId, 'participants', id] as const,
              detail: [
                'store',
                scopeId,
                'items',
                'campaigns',
                itemId,
                'participants',
                id,
                'detail',
              ] as const,
              //** Activity log for this participant */
              activity: [
                'store',
                scopeId,
                'items',
                'campaigns',
                itemId,
                'participants',
                id,
                'activity',
              ] as const,
            });
          },
        }),
        teams: createQueryKey({
          all: ['store', scopeId, 'items', 'campaigns', itemId, 'teams'] as const,
          export: ['store', scopeId, 'items', 'campaigns', itemId, 'teams', 'export'] as const,
          id(id) {
            return createQueryKey({
              all: ['store', scopeId, 'items', 'campaigns', itemId, 'teams', id] as const,
              detail: [
                'store',
                scopeId,
                'items',
                'campaigns',
                itemId,
                'teams',
                id,
                'detail',
              ] as const,
            });
          },
        }),
        prizes: createQueryKey({
          all: ['store', scopeId, 'items', 'campaigns', itemId, 'prizes'] as const,
          id(id) {
            return createQueryKey({
              all: ['store', scopeId, 'items', 'campaigns', itemId, 'prizes', id] as const,
              detail: [
                'store',
                scopeId,
                'items',
                'campaigns',
                itemId,
                'prizes',
                id,
                'detail',
              ] as const,
            });
          },
        }),
        benefits: benefitKeys(['store', scopeId, 'items', 'campaigns', itemId, scopeId] as const),
      }),
  });

/** Query Keys for ['store', scopedId, items, *] */
const storeItemKeys = (scopeId: QueryKeyParam) =>
  createQueryKey({
    all: ['store', scopeId, 'items'] as const,
    /** Generic Store Item Ids */
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'items', itemId] as const,
        detail: ['store', scopeId, 'items', itemId, 'detail'] as const,
        inventory: inventoryKeys(scopeId, itemId),
      }),
    options: ['store', scopeId, 'items', 'options'] as const,
    /** Scoped Campaigns key */
    campaigns: campaignsKeys(scopeId),
    events: eventsKeys(['store', scopeId, 'items'] as const),
    fundraisers: fundRaiserKeys(scopeId),
    donations: donationKeys(scopeId),
    subscriptions: subscriptionKeys(scopeId),
    discounts: discountsKeys(['store', scopeId] as const),
    memberships: membershipsKeys(scopeId),
    studentFees: storeItemStudentFeesKeys(['store', scopeId] as const),
  });

/** Query keys for ['store', scopedId, causes, *] */
const causeKeys = (scopeId: QueryKeyParam) =>
  createQueryKey({
    all: ['store', scopeId, 'causes'] as const,
    id: (itemId: QueryKeyParam) =>
      createQueryKey({
        all: ['store', scopeId, 'causes', itemId] as const,
        detail: ['store', scopeId, 'causes', itemId, 'detail'] as const,
      }),
  });

/**
 * Query keys for ['store', scopeId, *]
 *
 * @param  scopeId is the scope in which we want to query the store with
 */
const scopedIdKeys = (scopeId: QueryKeyParam, inheritedKeys: QueryKey) =>
  createQueryKey({
    all: [...inheritedKeys, scopeId] as const,
    carts: cartKeys(scopeId),
    categories: categoryKeys(scopeId),
    consumer: consumerKeys(scopeId),
    items: storeItemKeys(scopeId),
    causes: causeKeys(scopeId),
    redeemable: redeemableTicketsKeys([...inheritedKeys, scopeId] as const),
    terminal: terminalCartKeys(scopeId),
    productOptionPresets: productOptionPresetsKeys([...inheritedKeys, scopeId] as const),
    additionalFieldPresets: additionalFieldPresetsKeys([...inheritedKeys, scopeId] as const),
  });

/** Query keys for ['store'] */
const storeKeys = createQueryKey({
  all: ['store'] as const,
  id: (id) => scopedIdKeys(id, ['store']),
});

export { storeKeys };
